import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { 
  Users, 
  MoreVertical, 
  UserCheck, 
  UserX, 
  Edit, 
  ChevronLeft, 
  ChevronRight,
  CheckSquare
} from "lucide-react";

const getStatusBadge = (status) => {
  switch (status) {
    case "active":
      return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Active</Badge>;
    case "inactive":
      return <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">Inactive</Badge>;
    case "transferred":
      return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Transferred</Badge>;
    case "graduated":
      return <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">Graduated</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
};

export function StudentsTable({ 
  students, 
  loading, 
  filters, 
  pagination, 
  setPagination, 
  canManage, 
  onEdit, 
  onUpdateStatus,
  onBulkUpdateStatus
}) {
  const [selectedIds, setSelectedIds] = useState([]);
  
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(students.map(s => s.id || s.student_id));
    } else {
      setSelectedIds([]);
    }
  };
  
  const handleSelectOne = (studentId, checked) => {
    if (checked) {
      setSelectedIds(prev => [...prev, studentId]);
    } else {
      setSelectedIds(prev => prev.filter(id => id !== studentId));
    }
  };
  
  const handleBulkAction = async (status) => {
    if (selectedIds.length === 0) return;
    if (onBulkUpdateStatus) {
      await onBulkUpdateStatus(selectedIds, status);
      setSelectedIds([]);
    }
  };
  
  const allSelected = students.length > 0 && selectedIds.length === students.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < students.length;
  
  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
          </div>
        ) : students.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-1">No students found</h3>
            <p className="text-slate-500 text-sm">
              {filters.search || filters.grade || filters.section || filters.status
                ? "Try adjusting your filters"
                : "Start by adding students or uploading a CSV"}
            </p>
          </div>
        ) : (
          <>
            {/* Bulk Actions Bar */}
            {selectedIds.length > 0 && canManage && (
              <div className="flex items-center justify-between px-4 py-3 bg-emerald-50 border-b border-emerald-200">
                <div className="flex items-center gap-2">
                  <CheckSquare className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-700">
                    {selectedIds.length} student{selectedIds.length > 1 ? 's' : ''} selected
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-green-600 border-green-300 hover:bg-green-50"
                    onClick={() => handleBulkAction("active")}
                  >
                    <UserCheck className="w-4 h-4 mr-1" />
                    Activate
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-slate-600 border-slate-300 hover:bg-slate-50"
                    onClick={() => handleBulkAction("inactive")}
                  >
                    <UserX className="w-4 h-4 mr-1" />
                    Deactivate
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => setSelectedIds([])}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {canManage && (
                      <th className="py-3 px-4 w-12">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onCheckedChange={handleSelectAll}
                          data-testid="select-all-students"
                        />
                      </th>
                    )}
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Student</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Class</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Roll No.</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Parent</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {students.map((student) => {
                    const studentId = student.id || student.student_id;
                    const isSelected = selectedIds.includes(studentId);
                    return (
                    <tr key={studentId} className={`hover:bg-slate-50 ${isSelected ? 'bg-emerald-50' : ''}`} data-testid={`student-row-${student.student_id}`}>
                      {canManage && (
                        <td className="py-3 px-4">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) => handleSelectOne(studentId, checked)}
                            data-testid={`select-student-${student.student_id}`}
                          />
                        </td>
                      )}
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-slate-800">{student.name}</p>
                          <p className="text-xs text-slate-500">{student.student_id}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-slate-700">Grade {student.grade} - {student.section}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-slate-600">{student.roll_number || "-"}</span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm">
                          <p className="text-slate-700">{student.parent_name || "-"}</p>
                          <p className="text-xs text-slate-500">{student.parent_email || "-"}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {getStatusBadge(student.status)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {canManage && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`student-actions-${student.student_id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => onEdit(student)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {student.status === "active" ? (
                                <DropdownMenuItem onClick={() => onUpdateStatus(student.id, "inactive")}>
                                  <UserX className="w-4 h-4 mr-2" />
                                  Mark Inactive
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => onUpdateStatus(student.id, "active")}>
                                  <UserCheck className="w-4 h-4 mr-2" />
                                  Mark Active
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => onUpdateStatus(student.id, "transferred", "Transferred to another school")}>
                                Transfer Out
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => onUpdateStatus(student.id, "graduated")}>
                                Mark Graduated
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
              <p className="text-sm text-slate-600">
                Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.skip === 0}
                  onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.skip + pagination.limit >= pagination.total}
                  onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default StudentsTable;
